import LocalDate from 'root-packages/utils/localDate/index.js'
import PhoneNumber from 'root-core/utils/phoneNumber/index.js'
import emailRegex from 'regex-email'
import format, {formatWithDictionary} from 'root-core/utils/text/stringFormatFilter.js'
import moment from 'moment'
import parseCountryFromAddress from '../parseCountryFromAddress.js'
import passengersSchemaFunctions from './passengersSchemaFunctions.js'
import * as priceFrequentTravellerDiscount from '../price-engine/priceFrequentTravellerDiscount.js'
import spacecase from 'root-core/utils/text/spacecase.js'
import titles from 'root-config/resources/titles.js'
import * as validations from './validations.js'
import * as countries from 'root-config/resources/countries/index.js'
import {postcodeValidator, postcodeValidatorExistsForCountry} from 'postcode-validator'

class BaseTabSchema {
  constructor (options) {
    this.bookingDate = options.bookingDate
    this.config = options.config
    this.departure = options.departure
    this.hasFlights = options.hasFlights
    this.hasPromisedFlights = options.hasPromisedFlights
    this.passengersSchemaFunctions = passengersSchemaFunctions(this.config)
  }

  addressLine (field, min, max) {
    return (value, is) => {
      if (value) {
        is(this.passengersSchemaFunctions.minLength(min, field)) &&
        is(this.passengersSchemaFunctions.maxLength(max, field)) &&
        is(validations.notags(this.config.errorMessages.pattern, field))
      }
    }
  }

  clientAddressMandatory (object) {
    return this.config.features.clientAddressMandatory ||
      object.postcode ||
      object.region ||
      object.city ||
      object.line1 ||
      object.line2
  }

  country (field) {
    return (value, is) => {
      is(this.passengersSchemaFunctions.present(field))
    }
  }

  email (field) {
    return (value, is) => {
      const {clientAddressMandatory, contactDetails} = this.config.features
      const isMandatory = clientAddressMandatory || contactDetails.clientEmailAddressMandatory

      if (isMandatory ? is(this.passengersSchemaFunctions.present(field)) : value) {
        if (!emailRegex.test(value.trim())) {
          return this.config.errorMessages.email
        }
      }
    }
  }

  frequentTravellerDiscount () {
    return validations.ensure(
      format(this.config.errorMessages.frequentTravellerDiscount, this.config.brandFamilyDisplayText),
      (value, is, object) => {
        return (
          value === false ||
          object.passengers.some(p => p.frequentTravellerDiscount.selected)
        )
      }
    )
  }

  ftdTourInfoSelects (field) {
    return (value, _, {selected, tourDateMonth, tourDateYear}) => {

      if (!selected) return

      if (!value || value == '0') {
        return `Please select a ${field}`
      }

      const today = new LocalDate()
      const currentYear = today.getFullYear()
      const currentMonth = today.getMonth() + 1 //JS months are zero based

      if (field === 'month' && tourDateMonth > currentMonth && tourDateYear >= currentYear) {
        return 'You have selected a date in the future, please choose another date'
      }
    }
  }

  ftdTourMonthSelected () {
    return this.ftdTourInfoSelects('month')
  }

  ftdTourYearSelected () {
    return this.ftdTourInfoSelects('year')
  }

  ftdTourName () {
    return (value, is, field) => {
      if (!field.selected) return

      const ftd = priceFrequentTravellerDiscount.applicableFrequentTravellerDiscount(this.departure, this.bookingDate)

      if (!ftd) {
        return 'There is no Past Passenger Discount available on this departure'
      }

      is(this.passengersSchemaFunctions.present('tour name')) &&
      is(this.passengersSchemaFunctions.minLength(3, 'tour name')) &&
      is(this.passengersSchemaFunctions.maxLength(30, 'tour name')) &&
      is(this.passengersSchemaFunctions.pattern(/^[A-Za-z-.' ]+$/, 'tour name'))
    }
  }

  gender (field) {
    return (value, is) => {
      is(this.passengersSchemaFunctions.present(field))
    }
  }

  name (field) {
    return (value, is) => {
      is(this.passengersSchemaFunctions.present(field)) &&
      is(this.passengersSchemaFunctions.minLength(2, field)) &&
      is(this.passengersSchemaFunctions.maxLength(30, field)) &&
      is(this.passengersSchemaFunctions.pattern(/^[A-Za-z-.' ]+$/, field))
    }
  }

  specialRequests (field) {
    return (value, is, object) => {
      if (object.specialRequirements) {
        if(value) {
          is(this.passengersSchemaFunctions.maxLength(500, field))
        }
      }
    }
  }

  nameOptional (field) {
    return (value, is) => {
      if (value) {
        is(this.passengersSchemaFunctions.present(field)) &&
        is(this.passengersSchemaFunctions.maxLength(30, field)) &&
        is(this.passengersSchemaFunctions.pattern(/^[A-Za-z-.' ]+$/, field))
      }
    }
  }

  number () {
    return (value, is, object, property) => {
      if (is(this.passengersSchemaFunctions.present()) && !/^\d+$/.test(value)) {
        return format(this.config.errorMessages.number, spacecase(property))
      }
    }
  }

  passengerDateOfBirth () {
    return (value, is, object) => {
      if (object.type === 'child' || (
        this.config.features.dobRequired &&
        (this.hasFlights() || this.hasPromisedFlights() || this.departure.hasInternalFlights)
      )) {
        is(this.passengersSchemaFunctions.date)
      }

      if (!value) {
        return
      }

      const ageAtDeparture = moment(this.departure.startDate.date)
        .diff(this.passengersSchemaFunctions.parseDate(value), 'years')

      if (object.type === 'adult') {
        const adultAgeFrom = this.departure.adultAgeFrom || 18
        if (ageAtDeparture < adultAgeFrom) {
          return format(
            this.config.errorMessages.adultPermittedAgeMin,
            adultAgeFrom
          )
        }
      }

      if (object.type === 'child') {
        if (ageAtDeparture !== object.age) {
          return this.config.errorMessages.ageNotMatchingDOB
        }
        if (ageAtDeparture < this.departure.childAgeFrom || ageAtDeparture > this.departure.childAgeTo) {
          return format(
            this.config.errorMessages.childPermittedAgeRange,
            this.departure.childAgeFrom,
            this.departure.childAgeTo
          )
        }

        const ageAtBooking = moment().diff(this.passengersSchemaFunctions.parseDate(value), 'years')

        if (ageAtBooking < this.departure.childAgeFrom && ageAtDeparture >= this.departure.childAgeFrom) {
          return format(
            this.config.errorMessages.childAge4OnDateOfBooking5OnDateOfDeparture,
            ageAtBooking,
            ageAtDeparture,
            this.config.phone.general
          )
        }
      }
    }
  }

  phoneNumber () {
    return (value, is, object) => {
      if (this.config.features.contactDetails.clientPhoneNumberMandatory || value) {
        if (is(this.number()) && object.areaCode) {
          const length = (object.areaCode + object.number).length
          if (length < 5 || length > 11) {
            return this.config.errorMessages.phoneNumberInvalid
          }
        }
      }
    }
  }

  phoneNumberAsString () {
    return (value, is, passenger) => {
      if (this.config.features.contactDetails.clientPhoneNumberMandatory || value) {
        if (is(this.passengersSchemaFunctions.present(value))) {
          const country = parseCountryFromAddress(passenger.address)
          const number = new PhoneNumber(passenger.phoneNumber, country && country.alpha2)
          if (!number.internationalCodeIsValid()) {
            return this.config.errorMessages.phoneNumberInvalidInternationalCode
          }

          const length = number.toString().replace('+', '').length
          if (length < 8 || length > 14 || !number.isValid()) {
            return this.config.errorMessages.phoneNumberInvalid
          }
        }
      }
    }
  }

  phoneNumberCountryCode (field) {
    return (value, is) => {
      this.config.features.contactDetails.clientPhoneNumberMandatory ? is(this.passengersSchemaFunctions.present(field)) : null
    }
  }

  postcode (field) {
    return (value, is, object) => {
      (this.clientAddressMandatory(object) ? is(this.passengersSchemaFunctions.present(field)) : value) &&
      is(this.validZipCode(field))
    }
  }

  region (field) {
    return (value, is, object) => {
      (this.clientAddressMandatory(object) ? is(this.passengersSchemaFunctions.present(field)) : value) &&
      is(this.passengersSchemaFunctions.maxLength(50, field))
    }
  }

  requiredAddressLine (field, min, max) {
    return (value, is, object) => {
      (this.clientAddressMandatory(object) ? is(this.passengersSchemaFunctions.present(field)) : value) &&
      is(this.passengersSchemaFunctions.minLength(min, field)) &&
      is(this.passengersSchemaFunctions.maxLength(max, field)) &&
      is(validations.notags(this.config.errorMessages.pattern, field))
    }
  }

  title (field) {
    return (value, is, object) => {
      if (
        (is(this.passengersSchemaFunctions.present(field))) &&
        object.gender && object.title &&
        !titles.find(t => t.name === object.title && (t.gender === object.gender || !t.gender))
      ) {
        return this.config.errorMessages.titleGenderMismatch
      }
    }
  }

  type () {
    return (value) => {
      if (!['adult','child'].includes(value)) {
        return this.config.errorMessages.passengerTypeInvalid
      }
    }
  }

  withinAgeRestrictions () {
    return (value, is) => {
      if (is(this.passengersSchemaFunctions.date)) {
        const ageAtDeparture = moment(this.departure.startDate.date)
          .diff(this.passengersSchemaFunctions.parseDate(value), 'years')

        const ageOutsidePermittedRangeMsg = () => formatWithDictionary(
          this.config.errorMessages.ageOutsidePermittedRange,
          {
            ageFrom: this.departure.adultAgeFrom,
            ageTo: this.departure.adultAgeTo,
            phone: this.config.phone.general,
          }
        )

        if (ageAtDeparture < this.departure.adultAgeFrom) {
          return ageOutsidePermittedRangeMsg()
        }

        if (ageAtDeparture > this.departure.adultAgeTo) {
          const canGoOnReunion = this.config.errorMessages?.ageOutsidePermittedRangeProposeReunion
            && ageAtDeparture >= this.config.reunionTripAgeLimits?.ageFrom
            && ageAtDeparture < this.config.reunionTripAgeLimits?.ageTo
          if (!canGoOnReunion) {
            return ageOutsidePermittedRangeMsg()
          }

          return formatWithDictionary(
            this.config.errorMessages.ageOutsidePermittedRangeProposeReunion,
            {
              ageFrom: this.config.reunionTripAgeLimits.ageFrom,
              ageTo: this.config.reunionTripAgeLimits.ageTo,
              href: this.config.brandPages.reunionTrips || this.config.brandPages.brandWebsite,
            }
          )
        }
      }
    }
  }

  validZipCode (field) {
    return (value, is, object) => {
      let fullCountryData
      if(object.country === 'United Arab Emirates'){
        //workaround for the UAE suggested by the Payment Team
        fullCountryData = countries.findByName('United States Of America')
      } else {
        fullCountryData = countries.findByName(object.country)
      }

      const countryCode = fullCountryData?.alpha2?.toUpperCase()
      if (countryCode && postcodeValidatorExistsForCountry(countryCode)) {
        const trimmed = value && value.trim()
        if (trimmed && !postcodeValidator(trimmed, countryCode)) {
          return format(
            this.config.errorMessages.zipcodeInvalid
          )
        } else {
          return undefined
        }
      } else {
        is(this.passengersSchemaFunctions.maxLength(10, field))
      }
    }
  }
}

export default BaseTabSchema
